/**
 * Main application component that serves as the root component.
 * Handles the overall layout, dialog management, and Google Analytics initialization.
 */

import { Component, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { MenuComponent } from './components/menu/menu.component';
import { FooterComponent } from './components/footer/footer.component';
import { DataLayerService } from './services/data-layer.service';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { NavbarComponent } from './components/navbar/navbar.component';
import { SkipLinksComponent } from './components/skip-links/skip-links.component';
import { DialogComponent } from './components/dialog/dialog.component';
import { DialogService, DialogConfig } from './services/dialog.service';
import { CommonModule } from '@angular/common';

/**
 * Root component of the Symbox homepage application.
 *
 * @remarks This component serves as the main container for the entire application.
 * It handles the initialization of tracking services, manages global dialog state,
 * and provides the basic layout structure with navigation, content area, and footer.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    MenuComponent,
    FooterComponent,
    BreadcrumbComponent,
    NavbarComponent,
    SkipLinksComponent,
    DialogComponent,
  ],
})
export class AppComponent implements OnInit {
  /** @readonly The application title */
  title = 'homepage-symbox';

  /**
   * Creates an instance of AppComponent.
   *
   * @param {DataLayerService} dataLayerService - Service for managing Google Analytics data layer
   * @param {DialogService} dialogService - Service for managing application-wide dialogs
   */
  constructor(
    // DataLayer service is injected but initialization is now moved to ngOnInit
    private dataLayerService: DataLayerService,
    private dialogService: DialogService
  ) {}

  /**
   * Component initialization lifecycle hook.
   *
   * @remarks Initializes the Google Analytics data layer and configures
   * Google Analytics based on user's cookie preferences stored in localStorage.
   * Analytics are disabled by default and only enabled if user has explicitly
   * accepted tracking cookies.
   */
  ngOnInit() {
    // Initialize the DataLayer
    this.dataLayerService.initDataLayer();

    // Disabled Google Analytics unless User accepted usage of Tracking Cookies
    window['ga-disable-UA-126099838-1'] = true;
    if (localStorage.getItem('analytics') === 'enabled') {
      window['ga-disable-UA-126099838-1'] = false;
    }
  }

  /**
   * Gets the current dialog visibility state.
   *
   * @readonly
   * @returns {boolean} True if dialog is currently visible, false otherwise
   */
  get isDialogVisible(): boolean {
    return this.dialogService.isVisible();
  }

  /**
   * Gets the current dialog configuration.
   *
   * @readonly
   * @returns {DialogConfig} The current dialog configuration object
   */
  get dialogConfig(): DialogConfig {
    return this.dialogService.config();
  }

  /**
   * Closes the currently open dialog.
   *
   * @remarks Template method that delegates to the dialog service
   * to close any currently open dialog.
   */
  closeDialog(): void {
    this.dialogService.closeDialog();
  }
}
